import KaboodleToaster from "components/common/KaboodleToaster";
import type { ICDUpdateData } from "components/ICDCoding/icdAdapter";
import { claimToIcdObject } from "components/ICDCoding/icdAdapter";
import ICDCodingBlock from "components/ICDCoding/ICDCodingBlock";
import LoadingIcon from "components/Loading/LoadingIcon";
import { useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  useGetClaimByIdQuery,
  usePatchClaimByIdMutation,
  useGetClaimValidationsQuery,
} from "services/claims-api";
import { useDispatch } from "utils/redux";
import { setCurrentClaim } from "services/claimsDataSlice.ts";
import { setValidationState } from "reducers/main";

import ClaimsPageContainer from "./ClaimsPageContainer";

const ClaimICDCodingFormWrapper = () => {
  const { claimId } = useParams<{ claimId: string }>();
  const dispatch = useDispatch();

  const { data: claim, isLoading } = useGetClaimByIdQuery(
    { id: claimId },
    { skip: !claimId },
  );

  const { data: claimValidation } = useGetClaimValidationsQuery(
    { id: claimId },
    { skip: !claimId },
  );

  const [patchClaim] = usePatchClaimByIdMutation();

  useEffect(() => {
    if (!isLoading) {
      dispatch(setCurrentClaim(claim));
    }
  }, [dispatch, isLoading]);

  useEffect(() => {
    if (claimValidation) {
      dispatch(
        setValidationState({
          errors: claimValidation.filter((e) => e.level === "ERROR"),
          warnings: claimValidation.filter((e) => e.level === "WARN"),
        }),
      );
    }
  }, [claimValidation, dispatch]);

  const handleSaveIcdData = useCallback(
    async (data: ICDUpdateData) => {
      if (!claimId) return;

      try {
        await patchClaim({
          id: claimId,
          data,
          useDetail: false,
        });
      } catch (error) {
        console.error("Failed to update Claim ICD data:", error);
        KaboodleToaster.error("Failed to update ICD data");
      }
    },
    [claimId, patchClaim],
  );

  if (isLoading) {
    return (
      <div className="flex jc-c p-4">
        <LoadingIcon size="small" showText={false} />
      </div>
    );
  }

  return (
    <ClaimsPageContainer>
      <div className="data-block">
        <div className="data-block-header">ICD Coding</div>
        <ICDCodingBlock
          data={claim ? claimToIcdObject(claim) : undefined}
          isLoading={isLoading}
          onSave={handleSaveIcdData}
          showAcceptanceButtons={false}
        />
      </div>
    </ClaimsPageContainer>
  );
};

export default ClaimICDCodingFormWrapper;
